/**
 * components/forms/FormTemplatePreviewModal.tsx
 *
 * Full-screen preview modal that shows the real client form experience in dry-run mode.
 * Uses ClientCollect component with previewMode enabled.
 * Includes integrated send functionality via SendFormModal.
 *
 * Updated Feb 2026: Removed screeningQuestions from PreviewData (conditions are now inline).
 * Updated Feb 2026: Added logoUrl to previewData for the premium UX refactor.
 * Updated Apr 2026 (single logo source of truth): logoUrl now comes from users.logo_url
 *   via the useTemplatePreviewData hook (join) instead of form_templates.logo_url.
 * Updated Apr 2026: pass userId to useTemplatePreviewData so logo resolves from the viewer
 *   (fixes missing logo for templates assigned to a user by an admin).
 */

import { useState } from 'react';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import SendFormModal from './SendFormModal';
import ClientCollect from '../../pages/ClientCollect';
import { useTemplatePreviewData } from '../../hooks/useTemplatePreviewData';
import { Send, X } from 'lucide-react';
import { transformNeedsToRequestFields } from '../../utils/previewHelpers';
import type { PreviewData } from '../../utils/previewHelpers';

interface FormTemplatePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string | null;
  userId: string;
  onSendSuccess?: () => void;
  adminMode?: boolean;
}

export default function FormTemplatePreviewModal({
  isOpen,
  onClose,
  templateId,
  userId,
  onSendSuccess,
  adminMode = false,
}: FormTemplatePreviewModalProps) {
  const { template, userLogoUrl, orderedNeeds, loading, error } = useTemplatePreviewData(templateId, userId);
  const [sendModalOpen, setSendModalOpen] = useState(false);

  const handleSendClick = () => {
    setSendModalOpen(true);
  };

  const handleSendSuccess = () => {
    setSendModalOpen(false);
    onClose();
    onSendSuccess?.();
  };

  if (!isOpen) return null;

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-2xl p-8 shadow-soft-xl">
          <div className="text-center">
            <div className="text-primary-600 mb-4 flex justify-center">
              <Spinner size="md" />
            </div>
            <p className="text-neutral-600">Chargement de l'aperçu...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !template || orderedNeeds.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-2xl p-8 shadow-soft-xl max-w-md">
          <div className="text-center">
            <p className="text-error-600 mb-4">
              {error || 'Aucun élément à afficher dans ce formulaire'}
            </p>
            <Button onClick={onClose} variant="secondary">
              Fermer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Prepare preview data
  const previewData: PreviewData = {
    title: template.name,
    description: template.description,
    customMessage: null,
    logoUrl: userLogoUrl,
    fields: transformNeedsToRequestFields(orderedNeeds),
  };

  return (
    <>
      {/* Preview header bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-neutral-200 shadow-soft">
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              Aperçu : {template.name}
            </h2>
            <p className="text-sm text-neutral-600">
              Mode aperçu : aucune donnée ne sera enregistrée
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={onClose} variant="secondary">
              <X className="w-4 h-4 mr-2" />
              Fermer l'aperçu
            </Button>
            {!adminMode && (
              <Button onClick={handleSendClick}>
                <Send className="w-4 h-4 mr-2" />
                Envoyer ce formulaire
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Client form in preview mode */}
      <div className="fixed inset-0 z-40 pt-[88px] bg-gradient-to-br from-primary-50 to-primary-100">
        <ClientCollect previewMode={true} previewData={previewData} />
      </div>

      {/* Send form modal */}
      <SendFormModal
        isOpen={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        template={template}
        userId={userId}
        onSuccess={handleSendSuccess}
      />
    </>
  );
}
